import type { JSX } from 'react';

import { LinkStatsCard, NoticesCard, TelemetryCard } from '../app/telemetry/TelemetryCards';
import { FlyMap } from '../map/FlyMap';
import { GuidedPanel } from './Guided';
import { FlyHud } from './Hud';
import { InstrumentPanel } from './Instruments';
import { FlightToolbar } from './Toolbar';
import { useCommandRunner } from './commanding';

/**
 * 飞行操作台。
 *
 * 布局按 QGC 那一套重排：**一块主显示区 + 一条右侧动作栏**，整页不滚动。
 *
 * ## 为什么整页不滚动
 *
 * 旧版是文档流（从上到下依次排开，整页滚），代价是**关键读数会滚出视野**：
 * 飞行员往下滚去看参数表时，姿态与电压就走了。地面站里"我还能不能继续操作"
 * 靠的正是这两个 —— 它们不该是"滚回去看一眼"的东西。
 *
 * 所以现在是：主显示区（姿态 + 操作条 + 地图）自己占满高度固定不动，
 * 只有右侧栏内部滚动。页面本身不滚 —— 屏幕上的位置就是它的身份，永远不变。
 *
 * ## 为什么右侧栏是"动作 + 仪表"而不是只有动作
 *
 * 一次真实操作是"看姿态 → 确认能不能动 → 动手 → 再看结果"。把仪表和按钮放在
 * 同一列里，这四步的视线移动是一条竖线；分摊到屏幕两侧则每次都要横穿整个屏幕。
 *
 * 两个装配上的决定，写在它们会被破坏的地方：
 *
 * 1. **命令管理器只有一个**（`useCommandRunner`）。两个按钮同时下发时，
 *    队列、重传与 ACK 归属才有一致的视图。
 * 2. **命令管理器随本组件卸载销毁**。它订阅了车辆的事件总线，不销毁的话每换一次焦点机体
 *    就多一份永久订阅 —— 表现是"越用越卡"，没有任何报错。
 *
 * 弹窗宿主（`<ConfirmDialogProvider>`）不在这里挂：它由应用最外层挂载且**只挂一处**
 *（多挂一处会让同一次确认被渲染成两份）。这里只负责调用 `useConfirm()`。
 */
export function FlyConsole(): JSX.Element {
  const runner = useCommandRunner();

  return (
    /*
     * 宽屏（≥1280）下整块占满高度、页面不滚，只有右侧栏内部滚。
     * 窄于 1280 时退回**整页滚动**：两栏并排会各自被压到看不清，
     * 而地图一旦被压扁就没有替代品 —— 所以宁可让页面滚，也不让它塌。
     * `xl:overflow-hidden` 是这层的开关：宽屏不滚，窄屏滚。
     */
    /*
     * 两栏的断点是 `lg`（1024px）而不是 `xl`（1280px）。
     *
     * 1280 太高了：1080 宽是一个很常见的窗口尺寸（1366×768 的笔记本、或半屏并排），
     * 而在那个宽度下退回单栏意味着右侧那一列仪表被推到地图下方 **3900px 处** ——
     * 飞行时最该一眼看到的东西变成了"要滚很久才到"。
     * 这是桌面软件，1024 起就是它该有的样子。
     */
    <div className="min-h-0 flex-1 overflow-auto p-3 lg:overflow-hidden">
      <div className="grid gap-3 lg:h-full lg:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)]">
        {/* 主显示区：姿态 → 操作条 → 地图。地图拿剩下的全部高度，且有不低于 320px 的下限 */}
        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          <FlyHud />
          <FlightToolbar runner={runner} />
          <FlyMap />
        </div>

        {/*
         * 右侧动作栏。宽屏下它是**唯一**可滚动的区域，且滚动条贴在它自己身上：
         * 唯一能滚的地方越明显，误以为"整页卡住了"的机会越小。
         * 窄屏下不给自己加滚动 —— 两处滚动条会让人分不清在滚哪一层。
         */}
        <div className="flex min-h-0 min-w-0 flex-col gap-3 lg:overflow-auto lg:pr-0.5">
          <InstrumentPanel />
          <GuidedPanel runner={runner} />
          <NoticesCard />
          <TelemetryCard />
          <LinkStatsCard />
        </div>
      </div>
    </div>
  );
}
